/* Aria Robotics — App-Shell: Theme, Router, Nav, Footer, Banner.
 * Ported aus app.jsx; Routing jetzt über react-router HashRouter (gleiche URLs). */
import * as React from 'react';
import { HashRouter, Routes, Route, useLocation, useParams, Navigate, Outlet } from 'react-router-dom';
import { Container, Btn, Icon, go, res } from '@/ui';
import { AriaData as D } from '@/data';
import { Home } from '@/pages/Home';
import { Product } from '@/pages/Product';
import { About, Service } from '@/pages/Company';
import { News, Blog, Article } from '@/pages/Content';
import { Contact, Privacy, Imprint } from '@/pages/Legal';
import { AuthProvider } from '@/admin/AuthContext';
import { ToastProvider } from '@/admin/ui';
import { ProtectedRoute, AdminOnlyRoute } from '@/admin/ProtectedRoute';
import { AdminLayout } from '@/admin/AdminLayout';
import { Login } from '@/admin/Login';
import { Dashboard } from '@/admin/Dashboard';
import { Users } from '@/admin/Users';
import { BlogList } from '@/admin/BlogList';
import { BlogEditor } from '@/admin/BlogEditor';
import { Categories } from '@/admin/Categories';
import { Settings } from '@/admin/Settings';

type Theme = 'light' | 'dark';

interface NavLink { to: string; label: string; }
const NAV: NavLink[] = [
  { to: '/nestor-01', label: 'Nestor 01' },
  { to: '/ueber-uns', label: 'Über uns' },
  { to: '/service', label: 'Service' },
  { to: '/neuigkeiten', label: 'Neuigkeiten' },
  { to: '/blog', label: 'Blog' },
  { to: '/kontakt', label: 'Kontakt' },
];

function useTheme(): [Theme, () => void] {
  const stored = (typeof localStorage !== 'undefined' ? localStorage.getItem('aria-theme') : null) as Theme | null;
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const [theme, setTheme] = React.useState<Theme>(stored || (mql.matches ? 'dark' : 'light'));
  const [explicit, setExplicit] = React.useState<boolean>(!!stored);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  React.useEffect(() => {
    if (explicit) return;
    const fn = (e: MediaQueryListEvent) => setTheme(e.matches ? 'dark' : 'light');
    mql.addEventListener('change', fn);
    return () => mql.removeEventListener('change', fn);
  }, [explicit, mql]);

  const toggle = () => {
    setTheme((cur) => {
      const next: Theme = cur === 'dark' ? 'light' : 'dark';
      localStorage.setItem('aria-theme', next);
      return next;
    });
    setExplicit(true);
  };
  return [theme, toggle];
}

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }, [pathname]);
  return null;
}

function Nav({ theme, toggleTheme }: { theme: Theme; toggleTheme: () => void }) {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const { pathname } = useLocation();
  React.useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', fn); fn();
    return () => window.removeEventListener('scroll', fn);
  }, []);
  const logo = res(theme === 'dark' ? 'assets/logo-mark-inverse.svg' : 'assets/logo-mark.svg');
  const isActive = (to: string): boolean => {
    if (to === '/neuigkeiten') return pathname.startsWith('/neuigkeiten');
    if (to === '/blog') return pathname.startsWith('/blog');
    return pathname === to;
  };
  const nav = (to: string) => { setOpen(false); go(to); };

  return (
    <nav style={{ position: 'sticky', top: 0, zIndex: 50, background: 'var(--nav-bg)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: `1px solid ${scrolled ? 'var(--border-default)' : 'var(--border-subtle)'}`, transition: 'border-color var(--dur-base) var(--ease-standard)' }}>
      <Container>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 68 }}>
          <a href="#/" onClick={() => setOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            <img src={logo} width="30" height="30" alt="" />
            <span style={{ fontWeight: 700, color: 'var(--text-100)', letterSpacing: '-0.02em', fontSize: 18 }}>Aria&nbsp;Robotics</span>
          </a>

          <div className="ar-nav-desktop" style={{ display: 'flex', gap: 26, alignItems: 'center' }}>
            {NAV.map((n) => (
              <a key={n.to} href={`#${n.to}`} style={{ fontSize: 15, textDecoration: 'none', color: isActive(n.to) ? 'var(--primary-200)' : 'var(--text-200)', fontWeight: isActive(n.to) ? 600 : 400, transition: 'color var(--dur-fast) var(--ease-standard)' }}>{n.label}</a>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button aria-label="Design-Modus wechseln" onClick={toggleTheme} style={{ width: 40, height: 40, borderRadius: '50%', border: '1px solid var(--border-default)', background: 'var(--surface-card)', color: 'var(--text-100)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={18} />
            </button>
            <div className="ar-nav-desktop" style={{ display: 'flex' }}>
              <Btn variant="cta" size="sm" onClick={() => go('/kontakt')}>Vorbestellen</Btn>
            </div>
            <button className="ar-hamburger" aria-label="Menü" onClick={() => setOpen((v) => !v)} style={{ display: 'none', width: 40, height: 40, borderRadius: '50%', border: '1px solid var(--border-default)', background: 'var(--surface-card)', color: 'var(--text-100)', cursor: 'pointer', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name={open ? 'x' : 'menu'} size={20} />
            </button>
          </div>
        </div>
      </Container>

      {open && (
        <div className="ar-nav-mobile" style={{ display: 'block', borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-card)' }}>
          <Container>
            <div style={{ padding: '12px 0 20px', display: 'flex', flexDirection: 'column' }}>
              {NAV.map((n) => (
                <button key={n.to} onClick={() => nav(n.to)} style={{ textAlign: 'left', background: 'none', border: 'none', padding: '14px 4px', fontFamily: 'var(--font-sans)', fontSize: 17, color: isActive(n.to) ? 'var(--primary-200)' : 'var(--text-100)', fontWeight: isActive(n.to) ? 600 : 400, borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}>{n.label}</button>
              ))}
              <div style={{ marginTop: 16 }}><Btn variant="cta" size="lg" fullWidth onClick={() => nav('/kontakt')}>Nestor 01 vorbestellen</Btn></div>
            </div>
          </Container>
        </div>
      )}
    </nav>
  );
}

function Footer() {
  const col = (title: string, links: [string, string][]) => (
    <div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginBottom: 14 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {links.map(([l, to]) => (
          <a key={l} href={`#${to}`} style={{ fontSize: 14.5, color: 'rgba(255,255,255,0.72)', textDecoration: 'none' }}>{l}</a>
        ))}
      </div>
    </div>
  );
  return (
    <footer style={{ background: 'var(--ink)', color: 'rgba(255,255,255,0.72)' }}>
      <Container>
        <div className="ar-footer-grid" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr', gap: 40, padding: '64px 0 40px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src={res('assets/logo-mark-inverse.svg')} width="30" height="30" alt="" />
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>Aria&nbsp;Robotics</span>
            </div>
            <p style={{ fontSize: 14.5, lineHeight: 1.6, marginTop: 16, maxWidth: '34ch', color: 'rgba(255,255,255,0.6)' }}>Dein Zuhause, in guten Händen. Wir bauen Roboter, denen man das Zuhause anvertraut.</p>
          </div>
          {col('Produkt', [['Nestor 01', '/nestor-01'], ['Service', '/service'], ['Vorbestellen', '/kontakt']])}
          {col('Unternehmen', [['Über uns', '/ueber-uns'], ['Neuigkeiten', '/neuigkeiten'], ['Blog', '/blog']])}
          {col('Rechtliches', [['Datenschutz', '/datenschutz'], ['Impressum', '/impressum'], ['Kontakt', '/kontakt']])}
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '18px 0', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <span style={{ color: 'var(--accent-100)', flex: 'none', marginTop: 1 }}><Icon name="sparkles" size={16} /></span>
          <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55, margin: 0, maxWidth: '90ch' }}>
            Hinweis nach EU-KI-Verordnung (EU AI Act): Teile der Texte und Bilder auf dieser Website wurden mit Unterstützung von Systemen künstlicher Intelligenz erstellt und redaktionell geprüft. KI-generierte Inhalte sind entsprechend gekennzeichnet.
          </p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: '20px 0 40px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>© 2026 {D.contact.company} · {D.contact.city}</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)' }}>aria-robotics.xyz</span>
        </div>
      </Container>
    </footer>
  );
}

function ConsentBanner() {
  const [show, setShow] = React.useState<boolean>(() => !localStorage.getItem('aria-consent'));
  if (!show) return null;
  const close = (v: string) => { localStorage.setItem('aria-consent', v); setShow(false); };
  return (
    <div style={{ position: 'fixed', left: 16, right: 16, bottom: 16, zIndex: 60, maxWidth: 560, margin: '0 auto', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', padding: 22 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span style={{ color: 'var(--primary-200)', flex: 'none', marginTop: 2 }}><Icon name="cookie" size={22} /></span>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-100)', margin: '0 0 6px' }}>Datenschutz & KI-Transparenz</h3>
          <p style={{ fontSize: 13.5, color: 'var(--text-200)', lineHeight: 1.55, margin: 0 }}>
            Wir verwenden nur technisch notwendige Cookies. Inhalte dieser Website wurden teils mit KI-Unterstützung erstellt (Kennzeichnung nach EU AI Act). Mehr in der <a href="#/datenschutz" style={{ color: 'var(--primary-200)' }}>Datenschutzerklärung</a>.
          </p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Btn variant="secondary" size="sm" onClick={() => close('essential')}>Nur notwendige</Btn>
        <Btn variant="cta" size="sm" onClick={() => close('all')}>Verstanden</Btn>
      </div>
    </div>
  );
}

function NewsArticle() {
  const { slug } = useParams();
  return <Article kind="news" slug={slug ?? ''} />;
}
function BlogArticle() {
  const { slug } = useParams();
  return <Article kind="blog" slug={slug ?? ''} />;
}

function PublicLayout() {
  const [theme, toggleTheme] = useTheme();
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <ScrollToTop />
      <Nav theme={theme} toggleTheme={toggleTheme} />
      <main style={{ flex: 1 }}>
        <Outlet />
      </main>
      <Footer />
      <ConsentBanner />
    </div>
  );
}

export function App() {
  return (
    <HashRouter>
      <AuthProvider>
        <ToastProvider>
          <Routes>
            {/* Public site */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/nestor-01" element={<Product />} />
              <Route path="/ueber-uns" element={<About />} />
              <Route path="/service" element={<Service />} />
              <Route path="/neuigkeiten" element={<News />} />
              <Route path="/neuigkeiten/:slug" element={<NewsArticle />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogArticle />} />
              <Route path="/kontakt" element={<Contact />} />
              <Route path="/datenschutz" element={<Privacy />} />
              <Route path="/impressum" element={<Imprint />} />
            </Route>

            {/* Admin */}
            <Route path="/admin/login" element={<Login />} />
            <Route path="/admin" element={<ProtectedRoute />}>
              <Route element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="blog" element={<BlogList />} />
                <Route path="blog/new" element={<BlogEditor />} />
                <Route path="blog/:id" element={<BlogEditor />} />
                <Route path="categories" element={<Categories />} />
                <Route element={<AdminOnlyRoute />}>
                  <Route path="users" element={<Users />} />
                  <Route path="settings" element={<Settings />} />
                </Route>
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ToastProvider>
      </AuthProvider>
    </HashRouter>
  );
}
